import {HttpClient} from '@angular/common/http';
import {computed, inject, Injectable, signal} from '@angular/core';
import {takeUntilDestroyed} from '@angular/core/rxjs-interop';
import {TranslateService} from '@ngx-translate/core';
import {SnackBarService} from '@shared/snackbar/snackbar.service';
import {getNormalizedValue} from '@shared/utils/utils';
import {catchError, EMPTY, map, Subject, switchMap} from 'rxjs';
import {Clinic, Configurations} from 'src/domain/clinic/clinic';
import {ClinicListItem} from 'src/domain/clinic/clinic-list';
import {ListResponse, ListState} from 'src/domain/list-response';
import {RequestStatus} from 'src/infrastructure/request-status.enum';

interface AdminClinicListState extends ListState<ClinicListItem, Clinic>
{
    listStatus: RequestStatus;
}

@Injectable({
    providedIn: 'root'
})
export class ClinicService
{
    private readonly httpClient = inject(HttpClient);

    public readonly snackBar = inject(SnackBarService);

    public readonly translate = inject(TranslateService);

    private readonly originalClinicList = signal<ClinicListItem[]>([]);

    private readonly state = signal<AdminClinicListState>({
        listStatus: RequestStatus.NOT_EXECUTED,
        status: RequestStatus.NOT_EXECUTED,
        items: [],
        total: 0,
        details: undefined
    });

    public readonly loadingList = computed(() => this.state().listStatus === RequestStatus.PENDING);

    public readonly loading = computed(() => this.state().status === RequestStatus.PENDING);

    public readonly fetched = computed(() => this.state().status === RequestStatus.CONCLUDED);

    public readonly fetchedList = computed(() => this.state().listStatus === RequestStatus.CONCLUDED);

    public readonly total = computed(() => this.state().total);

    public readonly clinics = computed(() => this.state().items);

    public readonly clinicDetails = computed(() => this.state().details);

    private readonly get$ = new Subject();

    private readonly listFetched$ = this.get$.pipe(
        switchMap(() =>
        {
            return this.httpClient.get<ListResponse<ClinicListItem>>('clinic').pipe(
                map(({total, items}: ListResponse<ClinicListItem>) => ({
                    total,
                    items: items.map(ClinicListItem.fromJSON)
                }))
            );
        })
    );

    private readonly getDetails$ = new Subject<string>();

    private readonly detailsFetched$ = this.getDetails$.pipe(
        switchMap((id: string) =>
        {
            return this.httpClient.get<Clinic>(`clinic/${id}`).pipe(
                catchError(() =>
                {
                    this.snackBar.showErrorSnackbar(this.translate.instant('GENERAL.RESPONSE_ERRORS.CLINIC_NOT_FOUND'));

                    return EMPTY;
                }),
                map(Clinic.fromJSON)
            );
        })
    );

    private readonly add$ = new Subject<Record<string, unknown>>();

    private readonly clinicAdded$ = this.add$.pipe(
        switchMap((clinic) =>
        {
            return this.httpClient.post('clinic', clinic).pipe(
                catchError(({error}) =>
                {
                    this.state.set({
                        ...this.state(),
                        status: RequestStatus.ERROR
                    });

                    this.snackBar.showErrorSnackbar(this.translate.instant(`GENERAL.RESPONSE_ERRORS.${error.code === 'CLINIC_ALREADY_REGISTERED' ? 'CLINIC_ALREADY_REGISTERED' : 'CLINIC_ADD_NOT_COMPLETE'}`));

                    return EMPTY;
                })
            );
        })
    );

    private readonly update$ = new Subject<{id: string; clinic: Record<string, unknown>}>();

    private readonly clinicUpdated$ = this.update$.pipe(
        switchMap(({id, clinic}) =>
        {
            return this.httpClient.patch(`clinic/${id}`, clinic).pipe(
                catchError(() =>
                {
                    this.state.set({
                        ...this.state(),
                        status: RequestStatus.ERROR
                    });

                    this.snackBar.showErrorSnackbar(this.translate.instant('GENERAL.RESPONSE_ERRORS.CLINIC_EDIT_NOT_COMPLETE'));

                    return EMPTY;
                }),
                map(() => id)
            );
        })
    );

    private readonly delete$ = new Subject<string>();

    private readonly clinicDeleted$ = this.delete$.pipe(
        switchMap((id: string) =>
        {
            return this.httpClient.delete(`clinic/${id}`).pipe(
                catchError(() =>
                {
                    this.state.set({
                        ...this.state(),
                        status: RequestStatus.ERROR
                    });

                    this.snackBar.showErrorSnackbar(this.translate.instant('GENERAL.RESPONSE_ERRORS.CLINIC_DELETE_NOT_COMPLETE'));

                    return EMPTY;
                })
            );
        })
    );

    private readonly patch$ = new Subject<{id: string; configurations: Configurations}>();

    private readonly configurationsPatched$ = this.patch$.pipe(
        switchMap(({id, configurations}) =>
        {
            //TODO change path when backend is ready
            return this.httpClient.patch(`clinic/${id}/configurations`, configurations).pipe(
                catchError(() =>
                {
                    this.state.set({
                        ...this.state(),
                        status: RequestStatus.ERROR
                    });

                    this.snackBar.showErrorSnackbar(this.translate.instant('GENERAL.RESPONSE_ERRORS.CLINIC_CONFIG_EDIT_NOT_COMPLETE'));

                    return EMPTY;
                }),
                map(() => id)
            );
        })
    );

    public constructor()
    {
        this.listFetched$.pipe(takeUntilDestroyed()).subscribe({
            next: ({items, total}) =>
            {
                this.originalClinicList.set(items);

                this.state.set({
                    ...this.state(),
                    listStatus: RequestStatus.CONCLUDED,
                    items,
                    total
                });
            }
        });

        this.detailsFetched$.pipe(takeUntilDestroyed()).subscribe({
            next: (clinic: Clinic) =>
            {
                this.state.set({
                    ...this.state(),
                    status: RequestStatus.CONCLUDED,
                    details: clinic
                });
            }
        });

        this.clinicUpdated$.pipe(takeUntilDestroyed()).subscribe({
            next: (updatedClinicId: string) =>
            {
                this.state.set({
                    ...this.state(),
                    status: RequestStatus.CONCLUDED
                });

                this.snackBar.showSuccessSnackbar(this.translate.instant('ADMIN.CLINICS.EDIT_SUCCESS'));

                this.getClinics();
                this.getDetails(updatedClinicId);
            }
        });

        this.clinicAdded$.pipe(takeUntilDestroyed()).subscribe({
            next: () =>
            {
                this.state.set({
                    ...this.state(),
                    status: RequestStatus.CONCLUDED
                });

                this.snackBar.showSuccessSnackbar(this.translate.instant('ADMIN.CLINICS.ADD_SUCCESS'));

                this.getClinics();
            }
        });

        this.clinicDeleted$.pipe(takeUntilDestroyed()).subscribe({
            next: () =>
            {
                this.state.set({
                    ...this.state(),
                    details: undefined,
                    status: RequestStatus.CONCLUDED
                });

                this.snackBar.showSuccessSnackbar(this.translate.instant('ADMIN.CLINICS.DELETE_SUCCESS'));

                this.getClinics();
            }
        });

        this.configurationsPatched$.pipe(takeUntilDestroyed()).subscribe({
            next: (updatedClinicId) =>
            {
                this.state.update((previousState) => ({
                    ...previousState,
                    status: RequestStatus.CONCLUDED
                }));

                this.snackBar.showSuccessSnackbar(this.translate.instant('ADMIN.CLINICS.CONFIG_SUCCESS'));
                
                //TODO maybe it will have a getConfigs() method
                this.getDetails(updatedClinicId);
            }
        });
    }

    public getClinics(): void
    {
        this.state.set({
            ...this.state(),
            listStatus: RequestStatus.PENDING
        });

        this.get$.next({});
    }

    public getDetails(id: string): void
    {
        this.state.set({
            ...this.state(),
            status: RequestStatus.PENDING
        });

        this.getDetails$.next(id);
    }

    public filterClinics(filter: string): void
    {
        const filteredClinics = this.originalClinicList().filter((clinic: ClinicListItem) => getNormalizedValue(clinic.name).includes(getNormalizedValue(filter)));

        this.state.set({
            ...this.state(),
            items: filteredClinics,
            total: filteredClinics.length
        });
    }

    public addClinic(clinic: Record<string, unknown>): void
    {
        this.state.set({
            ...this.state(),
            status: RequestStatus.PENDING
        });

        this.add$.next(clinic);
    }

    public updateClinic(id: string, clinic: Record<string, unknown>): void
    {
        this.state.set({
            ...this.state(),
            status: RequestStatus.PENDING
        });

        this.update$.next({id, clinic});
    }

    public deleteClinic(id: string): void
    {
        this.state.set({
            ...this.state(),
            status: RequestStatus.PENDING
        });

        this.delete$.next(id);
    }

    public patchConfigurations(id: string, configurations: Configurations): void
    {
        this.state.update((previousState) => ({
            ...previousState,
            status: RequestStatus.PENDING
        }));

        this.patch$.next({id, configurations});
    }

    public clearClinicDetails(): void
    {
        this.state.set({
            ...this.state(),
            details: undefined
        });
    }

    public clearState(): void
    {
        this.state.set({
            ...this.state(),
            status: RequestStatus.NOT_EXECUTED
        });
    }

    public updateClinicIntegrations(id: string, externalBookingLink: string): any
    {
        return this.httpClient.patch(`clinic/${id}`, {
            onlineIntegration: {
                externalBookingLink
            }
        }).pipe(
            catchError(() =>
            {
                this.snackBar.showErrorSnackbar(this.translate.instant('GENERAL.RESPONSE_ERRORS.CLINIC_EDIT_NOT_COMPLETE'));

                return EMPTY;
            }),
            map(() =>
            {
                this.snackBar.showSuccessSnackbar(this.translate.instant('ADMIN.CLINICS.INTEGRATIONS_SUCCESS'));
            })
        );
    }
}
